import type { SettlementRecord, SettlementSource, Worker } from "./types";
import { calculateSettlementAmounts, calculateWorkerPayable, settlementAdjustmentAmounts } from "./settlementAmounts";

export type SettlementTab = SettlementSource | "all";
export const sourceLabel: Record<SettlementSource, string> = { piecework: "월 수당", part_time: "단기 알바", contract: "계약" };
export const tabLabel: Record<SettlementSource, string> = { piecework: "월 수당 정산", part_time: "단기 알바 정산", contract: "계약 정산" };
export const statusLabel: Record<SettlementRecord["status"], string> = { pending: "미정산", batched: "정산 대기", paid: "정산 완료" };
export const today = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
export const money = (value: number) => `${Math.round(value).toLocaleString("ko-KR")}원`;
export const shortDateTime = (value: string) => new Intl.DateTimeFormat("ko-KR", { timeZone: "Asia/Seoul", dateStyle: "short", timeStyle: "short" }).format(new Date(value));
export const readAmount = (value: string): number | null => {
  if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)$/.test(value)) return null;
  const number = Number(value.replaceAll(",", "")); return Number.isSafeInteger(number) ? number : null;
};
/* 숫자만 남겨 놓고 다시 끊는다. 예전에는 이미 쉼표가 붙은 문자열을 그대로 검사했는데,
   "1,000"의 끝에 0을 더 치면 "1,0000"이 되어 검사에 걸리고, 걸리면 손도 안 대고 돌려주어
   그 모양이 그대로 남았다 — 쉼표가 만 단위에 찍힌 것처럼 보이던 이유다. */
export const formatAmount = (value: string) => {
  /* 쉼표만 걷고 나머지는 손대지 않는다. 걷어 낸 것이 숫자면 다시 끊고, 아니면(-100, 1.5)
     친 그대로 두어 검증이 잡게 한다 — 잘못 친 값을 말없이 고치면 그 사람은 자기가 무엇을
     보냈는지 모른다. */
  const bare = value.replaceAll(",", "");
  if (!bare) return "";
  if (!/^\d+$/.test(bare)) return value;
  const number = Number(bare);
  return Number.isSafeInteger(number) ? number.toLocaleString("ko-KR") : value;
};
export const validDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
export { bankAccountReady as bankReady } from "./settlementBankAccount";
export const mergeById = <T extends { id: string }>(current: T[], incoming: T[]): T[] => [...new Map([...current, ...incoming].map(value => [value.id, value])).values()];
type CalculationSource = Pick<SettlementRecord, "quantity" | "unitAmount" | "source" | "amount" | "adjustmentKind">;
/**
 * 명세서는 수량과 단가를 각자의 칸에 적는다. "50 × 270원"처럼 붙여 쓰면 어느 쪽이 수량인지
 * 열 이름이 알려 주지 못하고, 세로로 훑어 비교할 수도 없다. 계산에 쓸 값이 없는 행
 * (공제·추가 지급·관리자 입력액)은 빈 칸으로 두고, 근거는 작업 칸과 정산액이 말한다.
 */
export const calculationQuantity = (record: CalculationSource) =>
  record.adjustmentKind ? "-" : record.quantity !== null ? record.quantity.toLocaleString() : "-";
export const calculationUnit = (record: CalculationSource) =>
  record.adjustmentKind ? "-"
    : record.unitAmount !== null ? money(record.unitAmount)
    : record.source === "part_time" ? "관리자 입력액"
    : money(record.amount);
export const settlementTotals = (records: Pick<SettlementRecord, "workerId" | "grossAmount" | "adjustmentKind" | "adjustmentAmount" | "manualAmount">[]) => {
  const grouped = new Map<string, { gross: number; deduction: number; addition: number; manual: number }>();
  for (const record of records) {
    const value = grouped.get(record.workerId) ?? { gross: 0, deduction: 0, addition: 0, manual: 0 };
    const adjustment = settlementAdjustmentAmounts(record);
    value.gross += record.grossAmount; value.deduction += adjustment.deductionAmount; value.addition += adjustment.additionAmount;
    value.manual += record.manualAmount ?? 0;
    grouped.set(record.workerId, value);
  }
  return [...grouped.values()].reduce((total, value) => {
    const amounts = calculateSettlementAmounts(value.gross, value.deduction, value.addition);
    return {
      grossAmount: total.grossAmount + amounts.grossAmount,
      deductionAmount: total.deductionAmount + amounts.deductionAmount,
      additionAmount: total.additionAmount + amounts.additionAmount,
      withholdingAmount: total.withholdingAmount + amounts.withholdingAmount,
      payableAmount: total.payableAmount + calculateWorkerPayable(value.gross, value.deduction, value.addition, value.manual),
    };
  }, { grossAmount: 0, deductionAmount: 0, additionAmount: 0, withholdingAmount: 0, payableAmount: 0 });
};
export const attachmentAccept = ".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.hwp,.hwpx,.txt,.csv";
export function fileError(files: Pick<File, "name" | "size">[]) {
  if (files.length > 5) return "계약 파일은 최대 5개까지 첨부할 수 있습니다.";
  if (files.some(file => file.size > 10 * 1024 * 1024)) return "첨부 파일 하나의 크기는 10MB를 넘을 수 없습니다.";
  if (files.some(file => !attachmentAccept.split(",").includes(file.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? ""))) return "PDF, 이미지, 문서 또는 스프레드시트 파일만 첨부할 수 있습니다.";
  return "";
}
export type PayrollEntryDraft = { workerId: string; date: string; title: string; subtask: string; start: string; end: string; startTime: string; endTime: string; breakMinutes: string; hours: string; calculationType: "fixed" | "quantity"; quantity: string; amount: string; description: string };
/** 시급 한 건의 실근무 분. 휴게를 빼지 않으면 점심값까지 시급으로 나간다. */
export const workedMinutes = (draft: Pick<PayrollEntryDraft, "startTime" | "endTime" | "breakMinutes">) => {
  const at = (value: string) => /^\d{2}:\d{2}$/.test(value) ? Number(value.slice(0, 2)) * 60 + Number(value.slice(3)) : null;
  const start = at(draft.startTime), end = at(draft.endTime);
  if (start === null || end === null) return null;
  const minutes = end - start - (Number(draft.breakMinutes) || 0);
  return minutes > 0 ? minutes : null;
};
/** 시급 건만 다른 값을 받는다 — 나머지 둘(도급·월급)은 계약으로 저장되어 검사할 것이 같다. */
export function entryErrors(draft: PayrollEntryDraft, kind: "piece" | "monthly" | "hourly", workers: Pick<Worker, "id" | "kind">[]) {
  const errors: Record<string, string> = {}, amount = readAmount(draft.amount);
  if (!workers.some(worker => worker.id === draft.workerId && worker.kind === "human")) errors.workerId = "작업자를 선택해 주세요.";
  if (amount === null || amount <= 0 || amount > 1_000_000_000) errors.amount = "1원 이상 10억원 이하의 정수 금액을 입력해 주세요.";
  /* 프로젝트명은 갈래를 가리지 않고 필요하다 — 인형 작업이라 어떤 프로젝트의 돈인지 적혀
     있지 않으면 나중에 그 줄만 보고는 알 수 없다. */
  if (!draft.title.trim() || draft.title.trim().length > 100) errors.title = "프로젝트명을 1~100자로 입력해 주세요.";
  /* 하위공정은 도급에만 물린다. 도급은 1줄 = 1불출 = 1공정이라 적을 수 있고, 없으면
     나중에 그 줄만 보고 무슨 일의 돈인지 알 수 없다. 월급은 한 달에 공정이 수십 개라
     하나를 고를 수 없고, 시급은 시스템에 그 기록이 없어 적히면 좋지만 없어도 된다. */
  if (kind === "piece" && !draft.subtask.trim()) errors.subtask = "하위공정을 선택해 주세요.";
  /* 시급은 시간이 곧 근거다. 금액만 받으면 8시간 × 12,000을 사람이 암산해 96,000만 남고,
     나중에 그 줄만 보고는 맞는지 셀 수가 없다. */
  /* 시급은 일한 시간만 받는다 — 시작·종료·휴게로 되짚던 것을 현장에서는 그렇게 쓰지 않고
     "몇 시간"만 적는다고 한다. 0보다 크고 하루를 넘지 않으면 된다. */
  if (kind === "hourly") { const hours = Number(draft.hours); if (!Number.isFinite(hours) || hours <= 0 || hours > 24) errors.hours = "일한 시간을 적어 주세요."; }
  if (!validDate(draft.date)) errors.date = "올바른 정산일을 입력해 주세요.";
  if (kind !== "hourly") {
    if (!validDate(draft.start)) errors.start = "올바른 시작일을 입력해 주세요.";
    if (!validDate(draft.end) || draft.end < draft.start) errors.end = "종료일은 시작일 이후로 입력해 주세요.";
    if (draft.calculationType === "quantity" && (!/^\d+(?:\.\d+)?$/.test(draft.quantity) || Number(draft.quantity) <= 0 || Number(draft.quantity) > 1_000_000)) errors.quantity = "0보다 크고 1,000,000 이하인 수량을 입력해 주세요.";
  }
  return errors;
}
